<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createGcsConfig } from "./fs";
  import { makeFileSystemGcs, testGcsConnection } from "./fs-gcs";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createGcsConfig());
  let keyFileName = $state("");
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(config.key != "" && config.bucket.trim() != "" && !testingConnection);

  async function onKeyFile(ev: Event) {
    let input = ev.target as HTMLInputElement;
    let file = input.files?.[0];
    errorMessage = "";
    config.key = "";
    keyFileName = "";
    if (!file) {
      return;
    }
    if (file.size > 16 * 1024) {
      errorMessage = "Error: the key file is too long";
      return;
    }
    config.key = await file.text();
    keyFileName = file.name;
  }

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing google cloud storage connection");
    try {
      config.bucket = config.bucket.trim();
      let res = await testGcsConnection(config);
      config.email = res.email;
      if (config.name.trim() == "") {
        config.name = config.bucket;
      } else {
        config.name = config.name.trim();
      }
      let fs = makeFileSystemGcs(config);
      let who = config.name;
      telegramNotify(`New Google Cloud Storage connection for ${who}`);
      captureEvent("gcs-login", { bucket: config.bucket });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Google Cloud Storage">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-gcs-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-gcs-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-gcs-keyfile"
        >Service account key file: <span class="text-gray-400">(JSON)</span></label
      >
      <input type="file" id="input-gcs-keyfile" accept=".json,application/json" class="text-sm" onchange={onKeyFile} />
      {#if keyFileName}
        <div class="mt-1 max-w-64 truncate">{keyFileName}</div>
      {/if}

      <label class="mt-3" for="input-gcs-bucket">Bucket:</label>
      <input
        type="text"
        id="input-gcs-bucket"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.bucket}
      />

      <div class="mt-4 text-sm">
        <a
          href="https://cloud.google.com/iam/docs/keys-create-delete#creating"
          class="text-blue-600 underline"
          target="_blank"
          rel="noreferrer">Service account keys</a
        >
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 text-red-500 font-medium break-all w-72">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Google Cloud Storage</button>
    </div>
  {/snippet}
</DialogBase>
